import type {Point} from '@yandex-delivery/core/types'
import {flow, get} from '@yandex-delivery/utils'
import type {
  Action,
  ActionFeedback,
  ActionPerformerCall,
  PerformerPinDefault,
  PerformerPinLoadable,
  PerformerPositionResponseWithDirection,
  SharedRouteInfoResponse,
} from 'src/api/types'

import {type MapData, MapModes} from './types'

export const isPerformerCallAction = (action: Action): action is ActionPerformerCall => action.type === 'performer_call'
export const isFeedbackAction = (action: Action): action is ActionFeedback => action.type === 'feedback'
export const isPerformerImageLoadable = (
  pin: PerformerPinDefault | PerformerPinLoadable,
): pin is PerformerPinLoadable => 'image_tag' in pin

export const getMapProps = (
  info: SharedRouteInfoResponse,
  performer?: PerformerPositionResponseWithDirection,
): MapData => {
  const isPerformerSearch = Boolean(info.context.performer_search)
  const pin = performer ? get('pin', performer) : undefined
  const routePoints = get('route_points', info)
  const performerRoutePoints = get('performer_route.sorted_route_points', info)
  // @ts-expect-error CARGODEV-20330
  const hasUnvisitedPoint = routePoints.some(point => point.visit_status !== 'visited')
  // @ts-expect-error CARGODEV-20330
  const isWaitingPerformerAtSource = !isPerformerSearch && routePoints[0].visit_status !== 'visited'
  const route = (performerRoutePoints || routePoints).map((point, index) => ({
    point: point.coordinates as Point,
    index,
  }))
  // Показываем polyline если есть performer_route с >1 точкой ИЛИ routePoints >1
  const isRouteVisible = (performerRoutePoints?.length ?? 0) > 1 || routePoints.length > 1

  if (!performer && isPerformerSearch) {
    return {
      position: routePoints[0].coordinates as Point,
      mode: MapModes.SearchCourier,
      pin,
      isRouteVisible,
      route,
    }
  }

  let mode = MapModes.Transporting

  if (isPerformerSearch) {
    mode = MapModes.SearchCourier
  }

  if (isWaitingPerformerAtSource) {
    mode = MapModes.WaitCourier
  }

  if (!hasUnvisitedPoint) {
    mode = MapModes.Delivered
  }

  const direction = get('position.direction', performer)
  const mayBePerformerPosition = get('position', performer)
  const position: Point | undefined =
    mayBePerformerPosition?.lon != null && mayBePerformerPosition?.lat != null
      ? [mayBePerformerPosition.lon, mayBePerformerPosition.lat]
      : undefined

  return {
    bubbleText: get('bubble_text', performer),
    route,
    // может быть кейс когда есть позиция курьера, но нет точки куда ему идти
    // тогда маршрут строился как позиция курьера + оригинальный маршрут(все точки заказа)
    // т.е. если все точки посещены позицию курьера не показываем, чтобы карта не строила маршрут
    position: hasUnvisitedPoint ? position : undefined,
    direction,
    isRouteVisible,
    pin,
    mode,
  }
}

export const isPerformerPositionAvailable: (res?: SharedRouteInfoResponse) => boolean = flow(
  get('context.is_performer_position_available'),
  Boolean,
)
